import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { RootState } from '../../../../reducers';
import { fetchFiles } from '../../librarySlice';
import { FolderType } from '../../../../types';

interface FolderSelectorProps {
  onFolderChange: (folderId: string) => void;
  selectedFolderId: string | null;
  compact?: boolean;
  iconOnly?: boolean;
  pillStyle?: boolean;
}

const FolderSelector: React.FC<FolderSelectorProps> = ({
  onFolderChange,
  selectedFolderId,
  compact = false,
  iconOnly = false,
  pillStyle = false,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const { folders, team } = useSelector((state: RootState) => ({
    folders: state.library.folders.data,
    team: state.team.selectedTeam,
  }));

  const handleFolderChange = (folderId: string) => {
    onFolderChange(folderId);

    if (team?.teamId) {
      dispatch(
        fetchFiles({
          teamId: team.teamId,
          folderId,
          page: 1,
          limit: 20,
        }),
      );
    }
  };

  // Render folder options for shadcn Select
  const renderFolderOptions = (
    foldersList: FolderType[],
    level = 0,
  ): React.ReactNode[] => {
    return foldersList
      .map((folder) => {
        const prefix = level > 0 ? '\u00A0\u00A0'.repeat(level) : '';
        const option = (
          <SelectItem key={folder._id} value={folder._id}>
            {prefix}
            {folder.name}
          </SelectItem>
        );

        const childOptions: React.ReactNode[] = folder.children?.length
          ? renderFolderOptions(folder.children, level + 1)
          : [];

        return [option, ...childOptions];
      })
      .flat();
  };

  // Calculate trigger styles based on props
  const getTriggerClassName = () => {
    if (pillStyle) {
      return 'h-7 min-w-[54px] max-w-[54px] rounded-[14px]';
    }
    if (iconOnly) {
      return 'h-6 min-w-auto border-none';
    }
    if (compact) {
      return 'h-8';
    }
    return 'h-[52px]';
  };

  return (
    <div
      className={cn(
        'flex items-center',
        pillStyle || iconOnly || compact ? 'p-0' : 'px-2 py-6',
        compact || pillStyle || iconOnly ? 'h-auto' : 'h-[52px]',
      )}
    >
      <Select
        value={selectedFolderId || 'recents'}
        onValueChange={handleFolderChange}
      >
        <SelectTrigger className={cn('w-full text-sm', getTriggerClassName())}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="recents">
            {t(
              'drawer.multiAskAI.folderSelector.allRecordings',
              'All recordings',
            )}
          </SelectItem>
          {renderFolderOptions(folders)}
        </SelectContent>
      </Select>
    </div>
  );
};

export default FolderSelector;
